import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import Swal from 'sweetalert2';

import { useGetAiAgentCompanies } from '../../../../../shared/apis/ai-agent/company/reactQueries';
import { useCustomSearch } from '../../../../../shared/hook/useCustomSearch';
import * as C from '../../../../ui/common/CommonStyles';
import CustomSearch from '../../../../ui/search/customSearch';

import ServiceOperationSettingsModal from './ui/ServiceOperationSettingsModal';
import CompanyListTable from './ui/companyListTable';

import type { KeyboardEvent, MouseEvent } from 'react';
import type { IGetAiAgentCompanyListParams } from '../../../../../shared/apis/ai-agent/company/types';

type SearchEvent = MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>;

const INITIAL_SEARCH_TYPE = 'companyName';

const parseCompanyId = (value: string): number | undefined => {
  const trimmedValue = value.trim();

  if (!/^\d+$/.test(trimmedValue)) {
    return undefined;
  }

  const companyId = Number(trimmedValue);
  return Number.isSafeInteger(companyId) && companyId > 0 ? companyId : undefined;
};

export default function AiAgent() {
  const [searchParams, setSearchParams] = useSearchParams('');
  const page = searchParams.get('page') || '1';
  const size = searchParams.get('size') || '10';
  const searchWord = searchParams.get('keyword') || '';
  const searchType = searchParams.get('searchType') || INITIAL_SEARCH_TYPE;
  const [selectedSearchType, setSelectedSearchType] = useState(searchType);
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

  const { inputRef, handleSearch, resetWord, resetSearchInput } = useCustomSearch({
    initType: INITIAL_SEARCH_TYPE,
  });

  const sendData: IGetAiAgentCompanyListParams = {
    size: Number(size),
    page: Number(page),
    companyName: searchType === 'companyName' ? searchWord : undefined,
    companyId: searchType === 'companyId' ? parseCompanyId(searchWord) : undefined,
  };

  const { data } = useGetAiAgentCompanies(sendData);

  useEffect(() => {
    setSelectedSearchType(searchType);
  }, [searchType]);

  const handleListSearch = (event: SearchEvent) => {
    if ('key' in event && event.key !== 'Enter') {
      return;
    }

    const searchKeyword = inputRef.current?.value.trim() || '';

    if (selectedSearchType === 'companyId' && searchKeyword && parseCompanyId(searchKeyword) === undefined) {
      event.preventDefault();
      void Swal.fire({
        icon: 'error',
        title: '회사코드 입력 오류',
        text: '회사코드는 1 이상의 숫자만 입력해주세요.',
        confirmButtonText: '확인',
      });
      return;
    }

    void handleSearch(event, selectedSearchType);
  };

  const handleResetSearch = () => {
    setSelectedSearchType(INITIAL_SEARCH_TYPE);
    resetWord(INITIAL_SEARCH_TYPE);
  };

  const handleModalOpen = (companyId: number) => {
    setSelectedCompanyId(companyId);
    open();
  };

  return (
    <div>
      <C.TitleBox>
        <div className="title">AI-AGENT 서비스 관리</div>
      </C.TitleBox>
      <C.ListBox style={{ overflowX: 'visible' }}>
        <C.ControlBox>
          <CustomSearch
            resetSearchInput={resetSearchInput}
            resetSearch={handleResetSearch}
            searchType={selectedSearchType}
            setSearchType={setSelectedSearchType}
            inputRef={inputRef}
            handleSearch={handleListSearch}
            searchOptions={[
              { value: 'companyName', label: '거래처명' },
              { value: 'companyId', label: '회사코드' },
            ]}
          />
        </C.ControlBox>
        <CompanyListTable
          companyList={data?.result ?? []}
          totalPage={Math.ceil((data?.count ?? 0) / Number(size))}
          page={Number(page)}
          setSearchParams={setSearchParams}
          searchParams={searchParams}
          onOpen={handleModalOpen}
        />
      </C.ListBox>
      {opened && <ServiceOperationSettingsModal opened={opened} onClose={close} selectedCompanyId={selectedCompanyId ?? 0} />}
    </div>
  );
}
